import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ArrowUpRight, ChevronRight, BookOpen, FileText } from "lucide-react";
import { renderSafeMarkdown } from "@/lib/safe-markdown";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { KnowledgeBaseArticle } from "@/components/marketing/KnowledgeBaseArticle";
import { HelpContactCTA } from "@/components/marketing/HelpContactCTA";
import {
  getHelpByPath,
  getAvailableHelpLocales,
  extractLocaleFromSplat,
  buildHelpHref,
  helpHrefBase,
  DEFAULT_LOCALE,
} from "@/lib/help.functions";

const SITE_URL = "https://banana.cards";
const SITE_NAME = "Banana Cards";

export const Route = createFileRoute("/help/$")({
  loader: async ({ params }) => {
    const splat = (params as any)._splat ?? "";
    try {
      const { locales } = await getAvailableHelpLocales();
      const { locale, rest } = extractLocaleFromSplat(splat, locales);
      if (!rest) {
        // Bare /help/{locale} → send to the help index for that locale
        // (English bare /help is handled by the index route, not this splat).
        throw redirect({ href: helpHrefBase(locale), statusCode: 301 });
      }
      const data = await getHelpByPath({ data: { path: rest, locale } });
      if ((data as any)?.type === "redirect") {
        throw redirect({ href: (data as any).to, statusCode: 301 });
      }
      return { splat, locale, rest, data, locales };
    } catch (e: any) {
      if (e?.isRedirect || e?.options?.href || e?.options?.to) throw e;
      return { splat, locale: DEFAULT_LOCALE, rest: splat, data: { type: "none" as const }, locales: [DEFAULT_LOCALE] };
    }
  },
  head: ({ loaderData }) => {
    const ld: any = loaderData ?? {};
    const data: any = ld.data;
    const locale: string = ld.locale ?? DEFAULT_LOCALE;
    const splat: string = ld.splat ?? "";

    if (!data || data.type === "none") {
      const last = String(splat).split("/").filter(Boolean).slice(-1)[0] ?? "Help";
      const pretty = last.replace(/-/g, " ").replace(/\b\w/g, (c: string) => c.toUpperCase());
      return {
        meta: [
          { title: `${pretty} — Help Center · ${SITE_NAME}` },
          { name: "description", content: `${pretty} guide and documentation for ${SITE_NAME}.` },
          { name: "robots", content: "noindex" },
        ],
      };
    }

    const canonicalByLocale: Record<string, string> = data.canonicalPathByLocale ?? {};
    const currentPath = canonicalByLocale[locale] ?? `/help/${splat}`;
    const url = `${SITE_URL}${currentPath}`.replace(/\/+$/, "");

    const alternateLinks = Object.entries(canonicalByLocale).map(([loc, p]) => ({
      rel: "alternate",
      hreflang: loc,
      href: `${SITE_URL}${p}`,
    }));
    if (canonicalByLocale[DEFAULT_LOCALE]) {
      alternateLinks.push({
        rel: "alternate",
        hreflang: "x-default",
        href: `${SITE_URL}${canonicalByLocale[DEFAULT_LOCALE]}`,
      });
    }

    const localeAlternates = Object.keys(canonicalByLocale)
      .filter((l) => l !== locale)
      .map((l) => ({ property: "og:locale:alternate", content: l }));

    const breadcrumbList = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Help Center", item: `${SITE_URL}${helpHrefBase(locale)}` },
        ...data.breadcrumbs.map((b: any, i: number) => ({
          "@type": "ListItem",
          position: i + 2,
          name: b.name,
          item: `${SITE_URL}${buildHelpHref(locale, data.breadcrumbs.slice(0, i + 1).map((x: any) => x.slug))}`,
        })),
      ],
    };

    if (data.type === "article") {
      const a = data.article;
      const title = a.meta_title || `${a.title} — Help Center · ${SITE_NAME}`;
      const description = a.meta_description || a.excerpt || `${a.title} — guide and documentation for ${SITE_NAME}.`;
      const articleLd = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: a.title,
        description,
        dateModified: a.updated_at,
        datePublished: a.updated_at,
        inLanguage: locale,
        mainEntityOfPage: url,
        author: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
        publisher: { "@type": "Organization", name: SITE_NAME, url: SITE_URL },
      };
      return {
        meta: [
          { title },
          { name: "description", content: description },
          { property: "og:title", content: a.title },
          { property: "og:description", content: description },
          { property: "og:type", content: "article" },
          { property: "og:url", content: url },
          { property: "og:site_name", content: SITE_NAME },
          { property: "og:locale", content: locale },
          ...localeAlternates,
          { property: "article:modified_time", content: a.updated_at },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: a.title },
          { name: "twitter:description", content: description },
        ],
        links: [{ rel: "canonical", href: url }, ...alternateLinks],
        scripts: [
          { type: "application/ld+json", children: JSON.stringify(articleLd) },
          { type: "application/ld+json", children: JSON.stringify(breadcrumbList) },
        ],
      };
    }

    const c = data.category;
    const title = c.meta_title || `${c.name} — Help Center · ${SITE_NAME}`;
    const description = c.meta_description || c.description || `${c.name} guides and articles for ${SITE_NAME}.`;
    const collectionLd = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: c.name,
      description,
      url,
      inLanguage: locale,
      isPartOf: { "@type": "WebSite", name: SITE_NAME, url: SITE_URL },
    };
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: c.name },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:site_name", content: SITE_NAME },
        { property: "og:locale", content: locale },
        ...localeAlternates,
        { name: "twitter:card", content: "summary" },
        { name: "twitter:title", content: c.name },
        { name: "twitter:description", content: description },
      ],
      links: [{ rel: "canonical", href: url }, ...alternateLinks],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(collectionLd) },
        { type: "application/ld+json", children: JSON.stringify(breadcrumbList) },
      ],
    };
  },
  component: HelpSplat,
});

function HelpSplat() {
  const { _splat } = Route.useParams() as { _splat: string };
  const initial = Route.useLoaderData();
  const locale: string = (initial as any)?.locale ?? DEFAULT_LOCALE;
  const rest: string = (initial as any)?.rest ?? _splat;
  const fetchByPath = useServerFn(getHelpByPath);
  const { data, isLoading } = useQuery({
    queryKey: ["help-path", locale, rest],
    queryFn: () => fetchByPath({ data: { path: rest, locale } }),
    initialData: (initial as any)?.data as any,
  });

  if (isLoading) return <HelpSkeleton />;

  if (!data || (data as any).type === "none" || (data as any).type === "redirect") {
    return (
      <SiteLayout>
        <section className="bg-background">
          <div className="mx-auto max-w-3xl px-4 py-32 text-center md:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">404</p>
            <h1 className="mt-3 font-display text-4xl md:text-5xl">Article not <em className="italic">found.</em></h1>
            <p className="mt-4 text-muted-foreground">We couldn't find that page.</p>
            <Link to={helpHrefBase(locale) as any} className="mt-6 inline-flex items-center gap-2 rounded-xl bg-foreground px-5 py-3 text-sm font-bold text-background">
              Back to Help Center <ArrowUpRight className="h-4 w-4" />
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  if ((data as any).type === "article") {
    const article = (data as any).article;
    const breadcrumbs = (data as any).breadcrumbs as Array<{ slug: string; name: string }>;
    const html = renderSafeMarkdown(article.body_md ?? "");
    // Build localized breadcrumb links for the KB article shell.
    const localizedCrumbs = breadcrumbs.map((b, i) => ({
      ...b,
      // KnowledgeBaseArticle constructs href via the base /help/... path; rebuild it ourselves below.
      __href: buildHelpHref(locale, breadcrumbs.slice(0, i + 1).map((x) => x.slug)),
    }));
    void localizedCrumbs;
    return (
      <KnowledgeBaseArticle
        breadcrumbs={breadcrumbs}
        title={article.title}
        excerpt={article.excerpt}
        updatedAt={article.updated_at}
        bodyHtml={html}
        // The default href builder inside KnowledgeBaseArticle uses "/help/"+slugs; we override via the locale-aware base.
        helpBase={helpHrefBase(locale)}
      />
    );
  }

  const d = data as any;
  const cat = d.category;
  const crumbHref = (i: number) =>
    buildHelpHref(locale, d.breadcrumbs.slice(0, i + 1).map((b: any) => b.slug));
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-10 pt-20 md:px-6">
          <nav className="flex flex-wrap items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">
            <Link to={helpHrefBase(locale) as any} className="transition hover:text-foreground">Help</Link>
            {d.breadcrumbs.map((b: any, i: number) => (
              <span key={b.slug} className="inline-flex items-center gap-1.5">
                <ChevronRight className="h-3 w-3 opacity-50" />
                {i === d.breadcrumbs.length - 1 ? (
                  <span className="text-foreground">{b.name}</span>
                ) : (
                  <Link to={crumbHref(i) as any} className="transition hover:text-foreground">{b.name}</Link>
                )}
              </span>
            ))}
          </nav>
          <h1 className="mt-4 font-display text-4xl leading-[1.05] md:text-6xl">{cat.name}</h1>
          {cat.description && (
            <p className="mt-5 max-w-2xl text-base text-muted-foreground md:text-lg">{cat.description}</p>
          )}
        </div>
      </section>

      {d.subcategories.length > 0 && (
        <section className="bg-muted">
          <div className="mx-auto max-w-5xl px-4 py-14 md:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Subtopics</p>
            <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {d.subcategories.map((s: any) => (
                <div key={s.id} className="group flex h-full flex-col rounded-2xl bg-card p-5">
                  <Link
                    to={buildHelpHref(locale, [...d.breadcrumbs.map((b: any) => b.slug), s.slug]) as any}
                    className="flex items-start justify-between gap-4"
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <BookOpen className="h-4 w-4 text-muted-foreground" />
                        <span className="font-display text-lg transition hover:underline">{s.name}</span>
                      </div>
                      {s.description && <p className="mt-1.5 line-clamp-2 text-sm text-muted-foreground">{s.description}</p>}
                    </div>
                    <ArrowUpRight className="h-4 w-4 transition group-hover:rotate-12" />
                  </Link>
                  {Array.isArray(s.articles) && s.articles.length > 0 && (
                    <ul className="mt-4 space-y-1.5 text-sm">
                      {s.articles.map((a: any) => (
                        <li key={a.id}>
                          <Link
                            to={buildHelpHref(locale, [...d.breadcrumbs.map((b: any) => b.slug), s.slug, a.slug]) as any}
                            className="flex items-center gap-1.5 text-foreground/80 transition hover:text-foreground hover:underline"
                          >
                            <ChevronRight className="h-3.5 w-3.5 opacity-50" />
                            <span className="line-clamp-1">{a.title}</span>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {d.articles.length > 0 ? (
        <section className={d.subcategories.length > 0 ? "bg-background" : "bg-muted"}>
          <div className="mx-auto max-w-5xl px-4 py-14 md:px-6">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Articles</p>
            <div className="mt-6 grid gap-3">
              {d.articles.map((a: any) => (
                <Link
                  key={a.id}
                  to={buildHelpHref(locale, [...d.breadcrumbs.map((b: any) => b.slug), a.slug]) as any}
                  className={`group flex items-start justify-between gap-4 rounded-2xl ${d.subcategories.length > 0 ? "bg-muted" : "bg-card"} p-5 transition hover:bg-foreground hover:text-background`}
                >
                  <div className="flex items-start gap-3">
                    <FileText className="mt-0.5 h-4 w-4 text-muted-foreground transition group-hover:text-background/70" />
                    <div>
                      <div className="font-bold">{a.title}</div>
                      {a.excerpt && <p className="mt-1 line-clamp-1 text-sm text-muted-foreground transition group-hover:text-background/70">{a.excerpt}</p>}
                    </div>
                  </div>
                  <ArrowUpRight className="mt-1 h-4 w-4 shrink-0 transition group-hover:rotate-12" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : d.subcategories.length === 0 ? (
        <section className="bg-muted">
          <div className="mx-auto max-w-5xl px-4 py-14 md:px-6">
            <div className="rounded-2xl bg-card p-10 text-center">
              <p className="font-display text-xl">Nothing here yet.</p>
              <p className="mt-2 text-sm text-muted-foreground">We're still writing articles for this section.</p>
            </div>
          </div>
        </section>
      ) : null}

      <HelpContactCTA
        surface={d.subcategories.length > 0 && d.articles.length > 0 ? "background" : "muted"}
      />
    </SiteLayout>
  );
}

function HelpSkeleton() {
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto max-w-5xl px-4 pb-10 pt-20 md:px-6">
          <div className="h-3 w-40 animate-pulse rounded-full bg-muted" />
          <div className="mt-5 h-12 w-3/4 animate-pulse rounded-2xl bg-muted" />
          <div className="mt-4 h-4 w-2/3 animate-pulse rounded-full bg-muted" />
        </div>
      </section>
      <section className="bg-muted">
        <div className="mx-auto max-w-5xl px-4 py-14 md:px-6">
          <div className="grid gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-20 animate-pulse rounded-2xl bg-card" />
            ))}
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}
